import * as React from 'react'
import styled from 'styled-components'

import { CircularButton } from './CircularButton'

import * as S from '../styles'
import { useLinkWrapper } from '../hooks/useLinkWrapper'

const MAX_NUMBER_OF_PAGES_TO_DISPLAY = 3 // Excluding first and last, which always display.

type PageProps = {
  linkComponent?: (props: { children: React.ReactNode; page: number }) => React.ReactElement | null
  href?: (page: number) => string
  onNavigate?: (toPage: number) => void
  page: number
  selected: boolean
  shallow: boolean
}

function Page({ linkComponent: Link, href, onNavigate, page, selected, shallow }: PageProps): React.ReactElement {
  const LinkWrapper = useLinkWrapper()

  const button = (
    <CircularButton
      forwardedAs={Link ? 'div' : 'a'}
      borderless={!selected}
      outlined={!selected}
      onClick={() => {
        if (onNavigate) {
          onNavigate(page)
        }
      }}
      small
    >
      {page}
    </CircularButton>
  )
  return (
    <S.pagination.Item selected={selected}>
      {Link && <Link page={page}>{button}</Link>}
      {!Link &&
        (href ? (
          <LinkWrapper scroll={false} href={href(page)} passHref shallow={shallow} legacyBehavior>
            {button}
          </LinkWrapper>
        ) : (
          <>{button}</>
        ))}
    </S.pagination.Item>
  )
}

export type PaginationProps = (
  | {
      href: (page: number) => string
      onNavigate?: (toPage: number) => void
    }
  | {
      href?: (page: number) => string
      onNavigate: (toPage: number) => void
    }
) & {
  linkComponent?: (props: { children: React.ReactNode; page: number }) => React.ReactElement | null
  // Total number of pages to display
  pageCount: number
  // Position of the selected page (starts at 1!)
  selectedPage: number
  // To enable shallow routing
  shallow?: boolean
} & React.OlHTMLAttributes<HTMLOListElement>

function PaginationComponent({
  linkComponent: Link,
  href,
  onNavigate,
  pageCount = 1,
  selectedPage = 1,
  shallow = true,
  ...olProps
}: PaginationProps): React.ReactElement {
  if (pageCount === 1) {
    return <ol {...olProps}></ol>
  }
  const pages = [...new Array(pageCount).keys()]
  const shouldSplitPagination = pageCount > MAX_NUMBER_OF_PAGES_TO_DISPLAY

  // Page number before which we may split
  const highestLimit = pageCount - MAX_NUMBER_OF_PAGES_TO_DISPLAY + 1
  // Page number after which we may split
  const lowestLimit = MAX_NUMBER_OF_PAGES_TO_DISPLAY
  // Difference between the two limits, which will be negative if you're displaying less pages than the allowed max.
  const range = highestLimit - lowestLimit

  const pageButtons = pages.reduce<JSX.Element[]>((pageAccumulator, index) => {
    const page = index + 1

    if (shouldSplitPagination) {
      const isAtLimit = page === 1 || page === pageCount
      const pageIsADirectSiblingOfSelected = selectedPage === page - 1 || selectedPage === page + 1

      /**
       * We want to display a button for the currently processed page if it either is:
       * - the first or the last page;
       * - the currently selected page;
       * - a direct sibling of the currently selected page;
       * - lower than page n° after which we split AND the currently selected page is too;
       * - higher than page n° before which we split BUT the currently selected page is strictly too AND we're not going above the max number of pages to display;
       * - higher than page n° before which we split BUT the currently selected page is too AND we're showing less pages than the max allowed. (i.e. negative highest limit)
       */
      if (
        isAtLimit ||
        page === selectedPage ||
        pageIsADirectSiblingOfSelected ||
        (page < lowestLimit && selectedPage < lowestLimit) ||
        (range >= 0 && page > highestLimit && selectedPage > highestLimit) ||
        (range < 0 && page > highestLimit && selectedPage >= highestLimit)
      ) {
        pageAccumulator.push(
          <Page
            key={page}
            href={href}
            linkComponent={Link}
            onNavigate={onNavigate}
            page={page}
            selected={selectedPage === page}
            shallow={shallow}
          />,
        )
      } else if (
        /**
         * We want to display the ellipsis item for the currently processed page if it either is:
         * - at a distance of 2 from the currently selected page;
         * - is the lowest page at which we need to split AND the selected page is lower than it;
         * - is the highest page at which we need to split AND the selected page is higher than it.
         */
        page - selectedPage === 2 ||
        selectedPage - page === 2 ||
        (page === lowestLimit && selectedPage < lowestLimit) ||
        (page === highestLimit && selectedPage > highestLimit)
      ) {
        pageAccumulator.push(<S.pagination.EllipsisItem key={page} />)
      }

      // Skip all pages that are neither displayed nor being transformed to an ellipsis.
      return pageAccumulator
    }

    pageAccumulator.push(
      <Page
        key={page}
        href={href}
        linkComponent={Link}
        onNavigate={onNavigate}
        page={page}
        selected={selectedPage === page}
        shallow={shallow}
      />,
    )

    return pageAccumulator
  }, [])

  return <ol {...olProps}>{pageButtons}</ol>
}

export const Pagination = styled(PaginationComponent)``
